'use client';

import { slugify } from '@schuelerhilfe/shared/src/utils/slugify';
import { useEffect, useRef, useState } from 'react';
import type { CollapsibleBlockResolved } from './types';
import { Icon, IconType } from '@/components/Icon';
import { SlotRenderer } from '@/components/SlotRenderer';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

/**
 * The article body's section unit, also placeable on a Page (spec 10.03.03.25).
 *
 * `<details>`/`<summary>` satisfies AC 6 with no extra work: the body is in the
 * initial HTML whatever the reader does, and its exposure to assistive
 * technology follows the visible state.
 *
 * With `forbidClosing` the element becomes a plain `<section>` rather than a
 * `<details open>`: a reader can click a `<details>` shut whatever the flag
 * says, and a disabled-looking toggle would advertise an interaction that does
 * nothing. The anchor and its scroll offset stay on the wrapper in both modes,
 * so every contents link keeps working.
 */
export const CollapsibleBlockComponent = ({
  resolved,
}: {
  resolved: CollapsibleBlockResolved;
}) => {
  const { title, blocks, anchorId, forbidClosing } = resolved;
  // The anchor stays on the **raw** title, so it matches the one the backend
  // derived for the contents list; only the visible heading is substituted.
  const ctx = useOptionalRenderContext();
  const sub = ctx?.substitutePlaceholders ?? ((value: string) => value);
  // The renderer's value wins because only it can keep anchors unique across
  // the page; otherwise the title's own slug. The shared slugifier *is* the
  // anchor rule, so the article's table of contents derives the identical
  // value from the same function.
  const anchor = anchorId ?? slugify(title);
  const ref = useRef<HTMLElement>(null);
  // Sections start expanded: the body is the article, not an optional aside.
  // Controlled rather than a bare `open` attribute so a re-render cannot
  // reopen a section the reader collapsed.
  const [open, setOpen] = useState(true);

  // The anchor sits on the wrapper itself, not on content inside it, so the
  // browser's own "expand to reveal the fragment target" never fires.
  useEffect(() => {
    if (!anchor) {
      return;
    }
    const openOnHash = () => {
      if (window.location.hash !== `#${anchor}` || !ref.current) {
        return;
      }
      // A `forbidClosing` section is already expanded, so the jump only
      // scrolls; `setOpen` on it would write state nothing reads.
      if (!forbidClosing) {
        setOpen(true);
      }
      ref.current.scrollIntoView();
    };

    /**
     * `hashchange` alone is not enough. It fires only when the hash actually
     * changes, so a reader who follows a contents entry, collapses that
     * section and then clicks the same entry again gets no event — and the
     * section stays shut (SH-204). Listening for the click as well covers the
     * same-hash case.
     *
     * Document-level and matched by href rather than wired to the contents
     * list: the anchors are derived server-side and the two components share
     * no runtime channel, so this also covers an in-body link or the author
     * chip pointing at the same section.
     *
     * No `scrollIntoView` here — the browser performs the fragment jump on the
     * click itself, and expanding only adds content *below* the summary, so
     * the heading it scrolled to does not move.
     */
    const openOnAnchorClick = (event: MouseEvent) => {
      if (forbidClosing) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a');
      if (!link) {
        return;
      }
      // `link.hash`/`link.pathname` are resolved by the DOM, so a relative and
      // an absolute href compare the same way.
      if (
        link.hash === `#${anchor}` &&
        link.pathname === window.location.pathname
      ) {
        setOpen(true);
      }
    };

    openOnHash();
    window.addEventListener('hashchange', openOnHash);
    document.addEventListener('click', openOnAnchorClick);
    return () => {
      window.removeEventListener('hashchange', openOnHash);
      document.removeEventListener('click', openOnAnchorClick);
    };
  }, [anchor, forbidClosing]);

  if (!title) {
    return null;
  }

  const heading = (
    <h2 className="text-primary flex-1 text-2xl font-semibold">{sub(title)}</h2>
  );
  // The section owns the width; `collapsible-block-body` in globals.css strips
  // each block's own centred reading-width container so the body lines up with
  // the title instead of being indented from it.
  const body = (
    <div className="collapsible-block-body mx-auto max-w-6xl px-4 pb-4 lg:px-9">
      <SlotRenderer blocks={blocks} zoneName="blocks" />
    </div>
  );

  if (forbidClosing) {
    return (
      <section
        id={anchor || undefined}
        ref={ref as React.RefObject<HTMLElement>}
        // Clears the sticky header, as in the collapsible mode below.
        className="scroll-mt-24"
      >
        <div className="mx-auto flex max-w-6xl items-center px-4 py-4 lg:px-9">
          {heading}
        </div>
        {body}
      </section>
    );
  }

  return (
    <details
      id={anchor || undefined}
      ref={ref as React.RefObject<HTMLDetailsElement>}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      // Clears the sticky header, which would otherwise cover the title a
      // contents entry just scrolled to. Honoured by both the fragment jump
      // and `scrollIntoView()` above.
      className="group scroll-mt-24"
    >
      <summary className="mx-auto flex max-w-6xl cursor-pointer list-none items-center gap-2 px-4 py-4 lg:px-9 [&::-webkit-details-marker]:hidden">
        {heading}
        <Icon
          src={IconType.SvgChevronDown}
          size={20}
          className="shrink-0 transition-transform group-open:rotate-180"
        />
      </summary>
      {body}
    </details>
  );
};
